import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { DialogBackdrop } from '../ui/dialog-backdrop';
import { Button } from '../ui/button';
import styles from './ConfirmDialog.module.css';

interface ConfirmDialogProps {
  title: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  children: ReactNode;
  tone?: 'default' | 'destructive';
  disabled?: boolean;
}

export const ConfirmDialog = ({
  title,
  confirmLabel,
  onConfirm,
  onClose,
  children,
  tone = 'destructive',
  disabled = false,
}: ConfirmDialogProps) => {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Focus lands on Cancel so an immediate Enter can't fire a destructive confirm, and Escape
  // closes to keep this as dismissible as the native confirm it replaced.
  useEffect(() => {
    cancelRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return createPortal(
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialogShell} open aria-modal="true" aria-labelledby={titleId}>
        <div className={styles.dialogPanel}>
          <div className={styles.body}>
            <div className={styles.headerRow}>
              {tone === 'destructive' && (
                <div className={styles.iconWrapper}>
                  <AlertTriangle className={styles.icon} />
                </div>
              )}
              <div>
                <h2 id={titleId} className={styles.title}>
                  {title}
                </h2>
                <div className={styles.description}>{children}</div>
              </div>
            </div>
          </div>
          <div className={styles.footer}>
            <Button ref={cancelRef} variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button variant={tone} size="sm" onClick={onConfirm} disabled={disabled}>
              {confirmLabel}
            </Button>
          </div>
        </div>
      </dialog>
    </>,
    document.body,
  );
};
